import React, { useState, useEffect, useRef } from 'react';
import { MoreIcon, LogOutIcon, XIcon, ChevronDownIcon } from '../common/Icons';
import './AppNavigation.css';

const MAX_TABS = 4; // Mobil alt çubukta en fazla 4 sekme; fazlası "Daha fazla" altında

const getInitials = (user) => {
  const source = user?.displayName || (user?.email || '').split('@')[0] || '';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const initials = parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2);
  return initials.toLocaleUpperCase('tr-TR') || '?';
};

// Dışarı tıklama ve Escape ile kapanan açılır menüler için
const useDismiss = (open, onClose, ref) => {
  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, ref]);
};

const AppNavigation = ({ items, activeTab, onSelect, user, roleName, onLogout }) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const accountRef = useRef(null);
  const sheetRef = useRef(null);

  useDismiss(accountOpen, () => setAccountOpen(false), accountRef);
  useDismiss(moreOpen, () => setMoreOpen(false), sheetRef);

  const needsMore = items.length > MAX_TABS;
  const tabItems = needsMore ? items.slice(0, MAX_TABS - 1) : items;
  const moreItems = needsMore ? items.slice(MAX_TABS - 1) : [];
  const moreActive = moreItems.some(i => i.key === activeTab);

  const select = (key) => {
    onSelect(key);
    setMoreOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-brand">
            <span className="app-brand-mark" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 10.5 12 3l9 7.5" />
                <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
              </svg>
            </span>
            <span className="app-brand-text">
              <span className="app-brand-name">Ay Home Lotus</span>
              <span className="app-brand-sub">Luxury Curtain</span>
            </span>
          </div>

          {items.length > 1 && (
            <nav className="app-nav" aria-label="Ana menü">
              {items.map(item => {
                const ItemIcon = item.icon;
                const active = item.key === activeTab;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`app-nav-item${active ? ' is-active' : ''}`}
                    data-section={item.key}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => select(item.key)}
                  >
                    <ItemIcon size={17} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          )}

          <div className="app-account" ref={accountRef}>
            <button
              type="button"
              className="app-account-btn"
              onClick={() => setAccountOpen(o => !o)}
              aria-expanded={accountOpen}
              aria-haspopup="menu"
              aria-label="Hesap menüsü"
            >
              <span className="app-avatar">{getInitials(user)}</span>
              <span className="app-account-text">
                <span className="app-account-email">{user?.email}</span>
                {roleName && <span className="app-account-role">{roleName}</span>}
              </span>
              <ChevronDownIcon size={16} className="app-account-chevron" />
            </button>

            {accountOpen && (
              <div className="app-menu" role="menu">
                <div className="app-menu-head">
                  <span className="app-avatar app-avatar-lg">{getInitials(user)}</span>
                  <span className="app-menu-id">
                    <strong>{user?.email}</strong>
                    {roleName && <span>{roleName}</span>}
                  </span>
                </div>
                <button type="button" role="menuitem" className="app-menu-item app-menu-danger" onClick={onLogout}>
                  <LogOutIcon size={17} /> Çıkış yap
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {items.length > 1 && (
        <nav className="app-tabbar" aria-label="Alt menü">
          {tabItems.map(item => {
            const ItemIcon = item.icon;
            const active = item.key === activeTab;
            return (
              <button
                key={item.key}
                type="button"
                className={`app-tab${active ? ' is-active' : ''}`}
                data-section={item.key}
                aria-current={active ? 'page' : undefined}
                onClick={() => select(item.key)}
              >
                <span className="app-tab-icon"><ItemIcon size={22} strokeWidth={active ? 2.1 : 1.8} /></span>
                <span className="app-tab-label">{item.shortLabel || item.label}</span>
              </button>
            );
          })}
          {needsMore && (
            <button
              type="button"
              className={`app-tab${moreActive || moreOpen ? ' is-active' : ''}`}
              onClick={() => setMoreOpen(o => !o)}
              aria-expanded={moreOpen}
            >
              <span className="app-tab-icon"><MoreIcon size={22} strokeWidth={2} /></span>
              <span className="app-tab-label">Daha fazla</span>
            </button>
          )}
        </nav>
      )}

      {moreOpen && (
        <div className="app-sheet-backdrop">
          <div className="app-sheet" ref={sheetRef} role="dialog" aria-label="Diğer sayfalar">
            <div className="app-sheet-head">
              <strong>Diğer sayfalar</strong>
              <button type="button" className="app-sheet-close" onClick={() => setMoreOpen(false)} aria-label="Kapat">
                <XIcon size={18} />
              </button>
            </div>
            <div className="app-sheet-grid">
              {moreItems.map(item => {
                const ItemIcon = item.icon;
                const active = item.key === activeTab;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`app-sheet-item${active ? ' is-active' : ''}`}
                    data-section={item.key}
                    onClick={() => select(item.key)}
                  >
                    <span className="app-sheet-icon"><ItemIcon size={20} /></span>
                    {item.label}
                  </button>
                );
              })}
            </div>
            <button type="button" className="app-sheet-logout" onClick={onLogout}>
              <LogOutIcon size={17} /> Çıkış yap
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppNavigation;
